/**
 * Microsoft Clarity analytics integration
 * 
 * Initializes Clarity tracking for production environments only.
 * Tracks educators (excludes students and super administrators).
 * 
 * Project ID is configured via VITE_CLARITY_PROJECT_ID environment variable
 * 
 * @see https://www.npmjs.com/package/@microsoft/clarity
 */

import clarity from "@microsoft/clarity"
import { getEnv } from "./env"
import { UserRole } from "../modules/reporting/types"
import { Role, User } from "../modules/mp-database"

/**
 * Determines if Clarity tracking should be enabled for the current context.
 * Checks for Clarity project ID, user role eligibility, and feature flag state.
 * 
 * Note: Tracking is controlled by whether VITE_CLARITY_PROJECT_ID is set in the environment.
 * This tracks in development, staging, and production environments.
 * Sessions can be filtered by domain in the Clarity dashboard.
 * 
 * @param role - The user's role
 * @param featureFlagEnabled - The enable_microsoft_clarity feature flag value.
 *   Caller is responsible for sourcing this from context or checkFeatureFlag.
 * @returns True if all conditions are met for Clarity tracking
 */
export const shouldTrackInClarity = (role: Role | undefined, featureFlagEnabled?: boolean): boolean => {
  if (!featureFlagEnabled) {
    return false
  }

  // Check if Clarity project ID is configured
  const clarityProjectId = getEnv("CLARITY_PROJECT_ID")
  if (!clarityProjectId) {
    return false
  }

  // Only track Teacher role (educators only)
  if (role !== UserRole.teacher) {
    return false
  }

  return true
}

/**
 * Initialize Microsoft Clarity tracking for the current user.
 * Only runs in production mode and for eligible user roles.
 * 
 * @param user - The authenticated user object
 * @param app - The application name ('ready' or 'workkeys') to determine which project ID to use
 * @param featureFlagEnabled - The enable_microsoft_clarity feature flag value
 */
export const initClarity = (
  user: User,
  app: "ready" | "workkeys",
  featureFlagEnabled: boolean,
  initialPathname?: string
): void => {
  if (!shouldTrackInClarity(user.role, featureFlagEnabled)) {
    return
  }

  // Get Clarity project ID from environment
  const clarityProjectId = getEnv("CLARITY_PROJECT_ID")

  try {
    // Initialize Clarity with project ID
    clarity.init(clarityProjectId)

    // Identify user with internal ID (no PII).
    // Pass the initial pathname so the first page is tracked with customPageId —
    // useClarityRouteTracking will be skipped on the initial render because
    // clarity.init() is gated behind an async feature flag HTTP request.
    if (user.id) {
      const friendlyName = `${user.firstName} ${user.lastName}`
      clarity.identify(user.id, undefined, initialPathname, friendlyName)
    }

    // Set tags for filtering/segmentation in Clarity dashboard
    clarity.setTag("role", user.role)
    clarity.setTag("app", app)

    // Optionally tag with district/tenant (non-PII organizational context)
    if (user.schoolDistrict) {
      clarity.setTag("district", user.schoolDistrict)
    }
  } catch (error) {
    console.error("[Clarity] Initialization error:", error)
  }
}
